import { ESuggestType } from '@cian/geosuggest-widget';

import { getRegionId } from '@/packages/Filters/shared/utils/geo';
import { FDealType, FOfferType, isSuburban } from '@/packages/JsonQuery';

import { type TOfferType } from '@/repositories/generated/shared/geo-suggest/v2/suggest';
import { type IGeoCodedSearchDetails } from '@/repositories/generated/shared/monolith-cian-realty/entities/Models/GeoCodedSearchDetails';
import { type IGeoCodedSearchResponse } from '@/repositories/generated/shared/monolith-cian-realty/entities/Models/GeoCodedSearchResponse';

import { type IInternalGeoCodedSearchDetails } from '@/microfrontends/filters/shared/containers/LEGACY/GeoSuggest/types';
import { prepareBoundedBy } from '@/microfrontends/filters/shared/mappers/prepareBoundedBy';
import { type TGeoType } from '@/microfrontends/filters/shared/types/LEGACY/geoSuggest';
import { type ILocation, type TLocation } from '@/microfrontends/filters/shared/types/location';

export function geoTypeMapper(geoType: ESuggestType): TGeoType {
  switch (geoType) {
    case ESuggestType.city:
      return 'location';
    case ESuggestType.district:
      return 'district';
    case ESuggestType.underground:
      return 'underground';
    case ESuggestType.newbuilding:
      return 'newobject';
    case ESuggestType.road:
      return 'highway';
    case ESuggestType.railway:
      return 'railway';
    case ESuggestType.village:
      return 'village';
    case ESuggestType.regionalDistrict:
      return 'regional_district_id';
    default:
      return 'location';
  }
}

export function getSuggestOfferType(offerType: FOfferType): TOfferType {
  if (isSuburban(offerType)) {
    return 'suburban';
  } else if (offerType === FOfferType.FlatNew) {
    return 'newobject';
  }

  return 'flat';
}

export function getBoundedBy(
  currentLocation: TLocation,
  regions: ILocation[],
): [YMaps.TCoord, YMaps.TCoord] | undefined {
  let location: ILocation | undefined;

  if (currentLocation === 'moscow_mo') {
    const region = regions.filter(r => r.id === 4593).shift();

    if (region) {
      location = region;
    }
  } else if (currentLocation === 'spb_lo') {
    const region = regions.filter(r => r.id === 4588).shift();

    if (region) {
      location = region;
    }
  } else if (!currentLocation.boundedBy && currentLocation.parentId) {
    const region = regions.filter(r => r.id === currentLocation.parentId).shift();

    if (region) {
      location = region;
    }
  } else {
    location = currentLocation;
  }

  if (!location || !location.boundedBy) {
    return undefined;
  }

  return [
    [location.boundedBy.upperCorner.lat, location.boundedBy.upperCorner.lng],
    [location.boundedBy.lowerCorner.lat, location.boundedBy.lowerCorner.lng],
  ] as [YMaps.TCoord, YMaps.TCoord];
}

export function isInternalGeoCodedSearchDetails(
  value: IGeoCodedSearchDetails,
): value is IInternalGeoCodedSearchDetails {
  return !!value.locationInfo;
}

export function getRegion(result: IGeoCodedSearchResponse): IInternalGeoCodedSearchDetails | undefined {
  return (result.details || []).reduce<IInternalGeoCodedSearchDetails | undefined>((acc, value) => {
    return isInternalGeoCodedSearchDetails(value) ? value : acc;
  }, undefined);
}

export function createLocationModel(detail: IInternalGeoCodedSearchDetails): ILocation {
  return {
    hasRegionalDistricts: false,
    ...detail.locationInfo,
    boundedBy: prepareBoundedBy(detail.locationInfo?.boundedBy || {}),
    displayName: detail.fullName || '',
    name: detail.name || '',
    id: detail.id,
    fullName: detail.fullName || '',
  };
}

// TODO: temporary solution, awaits for https://cianru.atlassian.net/browse/CD-11090
export const newbuildingsRegions = [
  -2, -1, 1, 2, 4560, 4561, 4566, 4567, 4574, 4580, 4584, 4585, 4588, 4593, 4596, 4598, 4603, 4606, 4608, 4609, 4612,
  4618, 4623, 4625, 4630, 4636,
];

export const isNewbuildingsRegion = (regionId: number): boolean => newbuildingsRegions.includes(regionId);

export const isNewbuildingsAvailable = (
  dealType: FDealType,
  offerType: FOfferType,
  currentLocation: TLocation,
): boolean =>
  !!(dealType & FDealType.Any && offerType & FOfferType.Urban && isNewbuildingsRegion(getRegionId(currentLocation)));

export const removeCountryName = (value: string): string => value.replace(/(^Россия, )|(, Россия$)/i, '');

export const getGeoSuggestLocation = (location: TLocation): number =>
  (location === 'moscow_mo' && -1) || (location === 'spb_lo' && -2) || (location as ILocation).id;
